import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

/**
 * My Work service: wraps MyWorkApi with retries + consistent
 * {success, data, meta, uniqueValues, message} shape used by the store.
 */
export class MyWorkService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    async _listCall(fn, errMessage) {
        try {
            const response = await withRetry(fn, { maxRetries: 2 });
            return {
                success: true,
                data: response.data,
                meta: response.pagination || response.meta || {},
                uniqueValues: response.uniqueValues,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, errMessage);
        }
    }

    getMyPending(params = {}) {
        return this._listCall(() => this.api.getMyPending(params), 'Failed to fetch my pending approvals');
    }

    getMySubmissions(params = {}) {
        return this._listCall(() => this.api.getMySubmissions(params), 'Failed to fetch my submissions');
    }

    getMyApprovalInvolvement(params = {}) {
        return this._listCall(() => this.api.getMyApprovalInvolvement(params), 'Failed to fetch my involvement');
    }

    getMyInformed(params = {}) {
        return this._listCall(() => this.api.getMyInformed(params), 'Failed to fetch informed items');
    }

    /**
     * Resolve pagination page for a pending My Work row (notification deep-link locate).
     * @param {{ focusEntityId: string, pageSize?: number }} params
     */
    async locateInList({ focusEntityId, pageSize = 15 }) {
        try {
            const response = await withRetry(
                () => this.api.getMyPending({ focusEntityId, pageSize }),
                { maxRetries: 2 },
            );
            return { focus: response.focus ?? { found: false } };
        } catch (error) {
            throw this._handleError(error, 'Failed to locate my-work pending row');
        }
    }

    async locateInformedInList({ focusEntityId, pageSize = 15 }) {
        try {
            const response = await withRetry(
                () => this.api.getMyInformed({ focusEntityId, pageSize }),
                { maxRetries: 2 },
            );
            return { focus: response.focus ?? { found: false } };
        } catch (error) {
            throw this._handleError(error, 'Failed to locate my-work informed row');
        }
    }

    async markInformedRead(id) {
        try {
            const response = await withRetry(() => this.api.markInformedRead(id), { maxRetries: 2 });
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to mark informed as read');
        }
    }

    async getByKindAndId(kind, id) {
        try {
            const response = await withRetry(() => this.api.getByKindAndId(kind, id), { maxRetries: 2 });
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch item');
        }
    }

    async approve(kind, id, data = {}) {
        try {
            const response = await this.api.approve(kind, id, data);
            return {
                success: true,
                data: response.data,
                flowComplete: response.flowComplete,
                message: response.message || 'Onaylandı'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to approve');
        }
    }

    async reject(kind, id, data = {}) {
        try {
            const response = await this.api.reject(kind, id, data);
            return { success: true, data: response.data, message: response.message || 'Reddedildi' };
        } catch (error) {
            throw this._handleError(error, 'Failed to reject');
        }
    }

    async returnToSubmitter(kind, id, data = {}) {
        try {
            const response = await this.api.returnToSubmitter(kind, id, data);
            return { success: true, data: response.data, message: response.message || 'Geri gönderildi' };
        } catch (error) {
            throw this._handleError(error, 'Failed to return');
        }
    }

    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) throw error;
        if (error.statusCode || error.status) throw ApiError.fromResponse(error);
        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
